"use client";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

const FLUSH_THRESHOLD_MS = 5000;
const IDLE_TIMEOUT_MS = 30000;
const TICK_MS = 1000;

export function EngagementTimeTracker() {
  const pathname = usePathname();
  const lastActivity = useRef(0);
  const pendingMs = useRef(0);
  const totalMs = useRef(0);
  const lastTick = useRef(0);

  useEffect(() => {
    pendingMs.current = 0;
    totalMs.current = 0;
    lastTick.current = Date.now();
    lastActivity.current = Date.now();
  }, [pathname]);

  useEffect(() => {
    const mark = () => {
      lastActivity.current = Date.now();
    };
    const activityEvents = ["pointerdown", "keydown", "pointermove", "scroll"] as const;
    for (const e of activityEvents) window.addEventListener(e, mark, { passive: true });

    const flush = () => {
      const seconds = Math.round(pendingMs.current / 1000);
      if (seconds <= 0) return;
      totalMs.current += pendingMs.current;
      pendingMs.current = 0;
      pushEvent(analyticsEvents.engagement_time, {
        seconds,
        path: pathname ?? location.pathname,
        total: Math.round(totalMs.current / 1000),
      });
    };

    const interval = window.setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastTick.current;
      lastTick.current = now;
      if (document.visibilityState !== "visible") return;
      if (now - lastActivity.current > IDLE_TIMEOUT_MS) return;
      pendingMs.current += elapsed;
      if (pendingMs.current >= FLUSH_THRESHOLD_MS) flush();
    }, TICK_MS);

    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
      else lastTick.current = Date.now();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);

    return () => {
      flush();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
      for (const e of activityEvents) window.removeEventListener(e, mark);
    };
  }, [pathname]);

  return null;
}
